import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const settingsApi = createApi({
  reducerPath: REDUCER_PATHS.SETTINGS,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    addMetaData: builder.mutation({
      query: (data) => ({
        url: apiConfig.META_DATA,
        body: data.postData,
        method: "POST",
      }),
      invalidatesTags: ["MetaData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editMetaData: builder.mutation({
      query: (data) => ({
        url: apiConfig.META_DATA + "/" + data?.id,
        body: data.postData,
        method: "PUT",
      }),
      invalidatesTags: ["MetaData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    metaData: builder.query({
      query: (id) => ({
        url: `${apiConfig.META_DATA}`,
        method: "GET",
      }),
      providesTags: ["MetaData"],
    }),

    addSiteSettingData: builder.mutation({
      query: (data) => ({
        url: apiConfig.SITE_SETTING,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["SiteSettingData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSiteSettingData: builder.mutation({
      query: (data) => ({
        url: apiConfig.SITE_SETTING + "/" + data?.id,
        body: data.postData,
        method: "PUT",
      }),
      invalidatesTags: ["SiteSettingData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    siteSettingData: builder.query({
      query: (id) => ({
        url: `${apiConfig.SITE_SETTING}`,
        method: "GET",
      }),
      providesTags: ["SiteSettingData"],
    }),
    addAnalyticalData: builder.mutation({
      query: (data) => ({
        url: apiConfig.ANALYTICAL_DATA,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["AnalyticalData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editAnalyticalData: builder.mutation({
      query: (data) => ({
        url: apiConfig.ANALYTICAL_DATA + "/" + data?.id,
        body: data.postData,
        method: "PUT",
      }),
      invalidatesTags: ["AnalyticalData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    analyticalData: builder.query({
      query: () => ({
        url: apiConfig.ANALYTICAL_DATA,
        method: "GET",
      }),
      providesTags: ["AnalyticalData"],
    }),
    addSiteUnMaintainanceData: builder.mutation({
      query: (data) => ({
        url: apiConfig.SITE_UN_MAINTENANCE,
        body: data.postData,
        method: "POST",
      }),
      invalidatesTags: ["SiteUnMaintainanceData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSiteUnMaintainanceData: builder.mutation({
      query: (data) => ({
        url: apiConfig.SITE_UN_MAINTENANCE + "/" + data?.id,
        body: data.postData,
        method: "PUT",
      }),
      invalidatesTags: ["SiteUnMaintainanceData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    siteUnMaintainanceData: builder.query({
      query: (id) => ({
        url: `${apiConfig.SITE_UN_MAINTENANCE}`,
        method: "GET",
      }),
      providesTags: ["SiteUnMaintainanceData"],
    }),
    dateTimeData: builder.query({
      query: () => ({
        url: apiConfig.DATE_TIME,
        method: "GET",
      }),
      providesTags: ["DateTimeData"],
    }),
    addDateTimeData: builder.mutation({
      query: (data) => ({
        url: apiConfig.DATE_TIME,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["DateTimeData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editDateTimeData: builder.mutation({
      query: (data) => ({
        url: apiConfig.DATE_TIME + "/" + data?.id,
        body: data.postData,
        method: "PUT",
      }),
      invalidatesTags: ["DateTimeData"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    socialMediaSdk: builder.query({
      query: () => ({
        url: apiConfig.SOCIAL_MEDIA_SDK,
        method: "GET",
      }),
      providesTags: ["SocialMediaSdk"],
    }),
    addSocialMediaSdk: builder.mutation({
      query: (data) => ({
        url: apiConfig.SOCIAL_MEDIA_SDK,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["SocialMediaSdk"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSocialMediaSdk: builder.mutation({
      query: (data) => ({
        url: apiConfig.SOCIAL_MEDIA_SDK + "/" + data?.id,
        body: data?.postData,
        method: "PUT",
      }),
      invalidatesTags: ["SocialMediaSdk"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    socialMediaLink: builder.query({
      query: () => ({
        url: apiConfig.SOCIAL_MEDIA_LINK,
        method: "GET",
      }),
      providesTags: ["SocialMediaLink"],
    }),
    addSocialMediaLink: builder.mutation({
      query: (data) => ({
        url: apiConfig.SOCIAL_MEDIA_LINK,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["SocialMediaLink"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSocialMediaLink: builder.mutation({
      query: (data) => ({
        url: apiConfig.SOCIAL_MEDIA_LINK + "/" + data?.id,
        body: data?.postData,
        method: "PUT",
      }),
      invalidatesTags: ["SocialMediaLink"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_STATUS,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    smtpDetails: builder.query({
      query: () => ({
        url: apiConfig.SMTP_DETAILS,
        method: "GET",
      }),
      providesTags: ["SmtpDetails"],
    }),
    addSmtpDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.SMTP_DETAILS,
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["SmtpDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSmtpDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.SMTP_DETAILS + "/" + data?.id,
        body: data?.postData,
        method: "PUT",
      }),
      invalidatesTags: ["SmtpDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    notification: builder.query({
      query: () => ({
        url: apiConfig.NOTIFICATION,
        method: "GET",
      }),
      providesTags: ["Notification"],
    }),
    notificationStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.NOTIFICATION,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Notification"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
  }),
});

export const {
  useAddMetaDataMutation,
  useEditMetaDataMutation,
  useMetaDataQuery,
  useAddSiteSettingDataMutation,
  useSiteSettingDataQuery,
  useEditSiteSettingDataMutation,
  useAddAnalyticalDataMutation,
  useEditAnalyticalDataMutation,
  useAnalyticalDataQuery,
  useAddSiteUnMaintainanceDataMutation,
  useSiteUnMaintainanceDataQuery,
  useDateTimeDataQuery,
  useAddDateTimeDataMutation,
  useSocialMediaSdkQuery,
  useAddSocialMediaSdkMutation,
  useSocialMediaLinkQuery,
  useAddSocialMediaLinkMutation,
  useChangeStatusMutation,
  useSmtpDetailsQuery,
  useAddSmtpDetailsMutation,
  useNotificationQuery,
  useNotificationStatusMutation,
  useEditSiteUnMaintainanceDataMutation,
  useEditDateTimeDataMutation,
  useEditSocialMediaLinkMutation,
  useEditSocialMediaSdkMutation,
  useEditSmtpDetailsMutation,
} = settingsApi;

export default settingsApi;
